
import { ViewState } from './types';

/**
 * Manages the state of views across the application
 */
export class ViewStateManager {
  private static instance: ViewStateManager;
  private cachedViews: Record<string, ViewState> = {};
  private listeners: ((views: Record<string, ViewState>, currentView: ViewState | null) => void)[] = [];
  private currentView: ViewState | null = null;

  private constructor() {}

  public static getInstance(): ViewStateManager {
    if (!ViewStateManager.instance) {
      ViewStateManager.instance = new ViewStateManager();
    }
    return ViewStateManager.instance;
  }

  /**
   * Get the current view
   */
  public getCurrentView(): ViewState | null {
    return this.currentView;
  }

  /**
   * Set the current view
   */
  public setCurrentView(view: ViewState): void {
    this.currentView = view;
    // Also cache it
    this.cachedViews[view.path] = view;
    this.notifyListeners();
  }

  /**
   * Get all cached views
   */
  public getCachedViews(): Record<string, ViewState> {
    return { ...this.cachedViews };
  }

  /**
   * Save scroll position for a view
   */
  public saveScrollPosition(path: string, position: number): void {
    const existingView = this.cachedViews[path];
    if (!existingView) return;
    
    this.cachedViews[path] = {
      ...existingView,
      scrollPosition: position
    };
    
    // If this is the current view, update it as well
    if (this.currentView?.path === path) {
      this.currentView = {
        ...this.currentView,
        scrollPosition: position
      };
    }
    
    this.notifyListeners();
  }

  /**
   * Update state for a view
   */
  public updateViewState(path: string, state: Record<string, any>): void {
    const existingView = this.cachedViews[path];
    if (!existingView) return;
    
    const updatedView = {
      ...existingView,
      state: {
        ...existingView.state,
        ...state
      }
    };
    
    this.cachedViews[path] = updatedView;
    
    // If this is the current view, update it as well
    if (this.currentView?.path === path) {
      this.currentView = updatedView;
    }
    
    this.notifyListeners();
  }

  /**
   * Get state for a view
   */
  public getViewState(path: string): Record<string, any> | undefined {
    return this.cachedViews[path]?.state;
  }

  /**
   * Clear view state (for a specific path or all)
   */
  public clearViewState(path?: string): void {
    if (path) {
      const newCachedViews = { ...this.cachedViews };
      delete newCachedViews[path];
      this.cachedViews = newCachedViews;
      
      // If this is the current view, set current view to null
      if (this.currentView?.path === path) {
        this.currentView = null;
      }
    } else {
      this.cachedViews = {};
      this.currentView = null;
    }
    
    this.notifyListeners();
  }

  /**
   * Subscribe to view state changes
   */
  public subscribe(listener: (views: Record<string, ViewState>, currentView: ViewState | null) => void): () => void {
    this.listeners.push(listener);
    
    // Immediately notify the new listener of the current state
    listener(this.cachedViews, this.currentView);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.cachedViews, this.currentView));
  }
}
